import { useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import {
  DocumentCheckIcon,
  CheckCircleIcon,
  EyeIcon,
  GlobeAltIcon,
  ScaleIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  ClipboardDocumentCheckIcon,
  BuildingLibraryIcon,
  MapIcon,
  EnvelopeIcon,
  ArrowTrendingUpIcon,
} from '@heroicons/react/24/outline';

/* ─── Animation Helpers ─── */
const ease = [0.44, 0, 0.56, 1] as const;

function FadeUp({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay, ease }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function FadeIn({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0 }}
      animate={isInView ? { opacity: 1 } : {}}
      transition={{ duration: 0.7, delay, ease }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function ScaleIn({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={isInView ? { opacity: 1, scale: 1 } : {}}
      transition={{ duration: 0.6, delay, ease }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ─── Data ─── */
const capabilities = [
  {
    icon: EyeIcon,
    title: 'Marketing Compliance Monitoring',
    description:
      'Every ad, email, social post, and disclosure checked against federal regulatory rules — Reg Z, UDAAP, Reg DD, ECOA, Fair Housing Act. Deterministic rules engine with AI-assisted detection and mandatory human approval. Examiner-ready audit trail on every check.',
  },
  {
    icon: DocumentCheckIcon,
    title: 'HMDA LAR Builder & Validation',
    description:
      'Validate and prepare LAR data for submission. 170+ CFPB edit checks across syntactical, validity, quality, and macro-quality categories with plain-language explanations. Field mapping templates for LOS imports. Data quality scoring, geocoding validation, and submission-ready exports.',
  },
  {
    icon: ScaleIcon,
    title: 'Fair Lending Analysis',
    description:
      'Full IFLEP-aligned Fair Lending Dashboard with 20+ risk factors across underwriting, pricing, steering, redlining, and marketing. Chi-square, Fisher\'s exact, and Welch\'s t-test statistical testing. Census tract mapping with demographic overlays and majority-minority tract classification. Regulator-aware methodology (FDIC / OCC / FRB).',
  },
  {
    icon: ChartBarIcon,
    title: 'Regression Analysis',
    description:
      'Logistic regression for denial and steering models. OLS regression for pricing disparity analysis. Matched-pair analysis for examiner file review. Full methodology documentation for examiner defensibility, regardless of which regulator is in the seat.',
  },
  {
    icon: ClipboardDocumentCheckIcon,
    title: 'CRA Module — Small, ISB, and Large Bank',
    description:
      'Three-test FDIC Large Bank methodology (Lending / Investment / Service) with 5-tier ratings and point-scoring composite. Multi-state and multistate-MSA rollups. Assessment area delineation with tract-level demographics. Community development activity tracking. The same methodology a CRA consulting engagement delivers — built into the platform, ready when the bank crosses the next asset threshold.',
  },
  {
    icon: GlobeAltIcon,
    title: 'Regulatory Change Intelligence',
    description:
      'Automated monitoring of CFPB, OCC, FDIC, and state-level enforcement actions and regulatory updates. Enforcement scraping with deduplication, high-value action flagging, compliance area tagging, and priority assignment. Alerts when new guidance affects your compliance program.',
  },
];

const interconnectivityFlows = [
  {
    title: 'HMDA LAR Data Flows Into Fair Lending',
    description: 'LAR entries are enriched with 50+ derived columns and fed directly into denial-rate, pricing, and geographic-disparity analyses — no re-upload, no re-entry across modules.',
  },
  {
    title: 'Fair Lending Findings Feed Exam Packages',
    description: 'Statistical findings, risk levels, and affected demographic groups are automatically pulled into one-click examiner-ready ZIP exports with full methodology documentation.',
  },
  {
    title: 'CRA Lending Tests Use Same HMDA Originations',
    description: 'The CRA Lending Test pulls origination volumes and geographic distribution from the same LAR data, with assessment area mapping and income-level classification — every module reads from one canonical loan-level source.',
  },
  {
    title: 'Multi-State Rollups for Multi-Footprint Banks',
    description: 'Banks operating across multiple states (Tennessee, Alabama, Florida, multistate MSAs like Memphis) get per-state CRA ratings rolled into one institution rating — deposit-weighted, examiner-defensible.',
  },
  {
    title: 'Board Reports Aggregate Every Module',
    description: 'Executive-level reports pull marketing compliance stats, fair-lending findings, CRA health scores, regression results, and regulatory updates into a single cross-module view ready for the board packet.',
  },
];

const smartBankFitPoints = [
  {
    icon: MapIcon,
    title: 'Multi-State Footprint, Multi-MSA Complexity',
    description:
      "Operating across Tennessee, Alabama, Florida — plus multistate MSAs like Memphis and Chattanooga — multiplies CRA assessment area work, state-level fair lending tracking, and HMDA reporting complexity. Clarid is built for exactly this shape: per-AA, per-state, and multistate-MSA rollups are first-class concepts, not retrofits.",
  },
  {
    icon: ArrowTrendingUpIcon,
    title: 'Approaching the Large Bank Threshold',
    description:
      "Banks crossing the $1.564B intermediate threshold — and especially those approaching $10B — face a step-change in CRA examination depth. The FDIC Large Bank three-test methodology (Lending / Investment / Service) with point-scoring composite is fundamentally different from the Small / ISB engine and lands fully built in Clarid. The same module that runs a Small Bank CRAD today scales to Large Bank without a re-platforming event.",
  },
  {
    icon: ShieldCheckIcon,
    title: 'Public-Company Compliance Scrutiny',
    description:
      "As a publicly-traded holding company (NASDAQ: SMBK), SmartFinancial faces heightened compliance documentation expectations from both regulators and shareholders. Clarid's examiner-ready audit trail on every action, board-grade reporting, and one-click exam package generation aligns with the documentation rigor a public-company compliance program requires.",
  },
  {
    icon: BuildingLibraryIcon,
    title: 'Community-Bank Identity at Scale',
    description:
      "\"The SmartBank Way\" is a community-bank identity executed across a multi-state footprint. Clarid was built for exactly this profile — community banks and credit unions that want the methodology rigor large-bank compliance vendors provide, without the per-engagement consulting cost or the multi-vendor stitching. The product is built by examiners and former consultants, not by general-purpose fintech engineers.",
  },
];

const dueDiligenceDocs = [
  'Security Whitepaper (CLARID-SEC-WP-001)',
  'Written Information Security Program / WISP (CLARID-SEC-001)',
  'Security Assessment with TLS, ZAP, nuclei, and TruffleHog results (CLARID-SA-001)',
  'NIST CSF 2.0 Risk Assessment — 85% composite (CLARID-RA-001)',
  'CSA STAR CAIQ v4.1 self-assessment (CLARID-CSA-001)',
  'Business Continuity Plan (CLARID-BCP-001)',
  'Vendor Inventory with concentration analysis (CLARID-VEN-001)',
  'AI Governance Policy + AI Incident Response Playbook',
  'Model Inventory with bias testing methodology',
  'Master Service Agreement, DPA, and SLA templates',
];

const nextSteps = [
  { num: 1, title: 'This Conversation', description: 'Walk through SmartBank\'s current compliance footprint and where Clarid would land first' },
  { num: 2, title: 'Platform Demo', description: 'Live walkthrough of the compliance health dashboard, fair lending analytics, HMDA validation, marketing compliance monitoring, and the CRA module' },
  { num: 3, title: 'Scoped Pilot', description: 'A defined pilot — typically one or two modules against a specific quarter\'s data — with clear success criteria and an executive readout' },
  { num: 4, title: 'Enterprise Activation', description: 'Roll Clarid out across the full SmartBank compliance program with onboarding support from Clarid\'s implementation team' },
];

/* ─── Component ─── */
export default function SmartBankPartnerPage() {
  useEffect(() => {
    document.title = 'SmartBank + Clarid — AI-Native Compliance for Community Banks';

    const metaTags: Record<string, string> = {
      'og:title': 'SmartBank + Clarid — Compliance Operating System',
      'og:description': 'AI-native compliance platform for community banks operating across multiple states. Built by examiners and consultants, with cross-module interconnectivity that legacy compliance vendors structurally can\'t match.',
      'og:type': 'website',
      'og:image': '/clarid-logo.png',
      'twitter:card': 'summary_large_image',
      'twitter:title': 'SmartBank + Clarid',
      'twitter:description': 'Compliance built for multi-state community banks.',
    };

    const existingTags: HTMLMetaElement[] = [];
    for (const [property, content] of Object.entries(metaTags)) {
      const meta = document.createElement('meta');
      meta.setAttribute('property', property);
      meta.setAttribute('content', content);
      document.head.appendChild(meta);
      existingTags.push(meta);
    }

    return () => {
      existingTags.forEach((tag) => document.head.removeChild(tag));
      document.title = 'Clarid — Clarity in every communication';
    };
  }, []);

  return (
    <div className="min-h-screen bg-white font-body text-slate-600">
      {/* ─── Minimal Header — Clarid only (prospect, not co-branded tech partner) ─── */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12 py-5 flex items-center">
          <img src="/clarid-logo.png" alt="Clarid" className="h-10" />
        </div>
      </header>

      {/* ─── Section 1: Hero ─── */}
      <section className="pt-36 pb-24 md:pt-48 md:pb-36">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12 text-center">
          <FadeUp>
            <p className="text-base font-semibold tracking-[0.2em] uppercase text-indigo-600 mb-3">
              Prepared for SmartBank
            </p>
            <p className="text-sm text-slate-400 mb-8">
              Steffani Jenkins · Community Reinvestment Director
            </p>
          </FadeUp>

          <FadeUp delay={0.1}>
            <h1 className="text-5xl md:text-6xl lg:text-7xl xl:text-[5.5rem] font-display font-bold text-navy-900 leading-[1.1] mb-10 max-w-5xl mx-auto">
              The Compliance Operating System for Banks Growing the SmartBank Way
            </h1>
          </FadeUp>

          <FadeUp delay={0.2}>
            <p className="text-xl md:text-2xl lg:text-[1.65rem] text-slate-500 max-w-4xl mx-auto leading-relaxed">
              Clarid is the AI-native compliance platform built for community banks operating across
              multiple states and multiple MSAs. Marketing compliance, HMDA, fair lending, CRA, and
              regulatory change — unified in one system, with examiner-ready audit trails on every
              action and the methodology depth of a consulting engagement built into the platform.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* ─── Section 2: Where SmartBank Is ─── */}
      <section className="py-24 md:py-36 bg-slate-50">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <div className="max-w-5xl mx-auto">
            <FadeUp>
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-14">
                Growth Adds Compliance Surface Area Faster Than Compliance Teams
              </h2>
            </FadeUp>

            <FadeUp delay={0.1}>
              <div className="space-y-7">
                <p className="text-xl md:text-[1.35rem] text-slate-600 leading-relaxed">
                  A community bank operating across Tennessee, Alabama, and Florida — with branches in
                  multistate MSAs like Memphis and Chattanooga — is, from a compliance standpoint, three
                  banks running in parallel. Three sets of state-specific rules. Multiple assessment
                  areas. Per-MSA fair lending dynamics. Multi-state HMDA volume. Marketing campaigns
                  that have to clear federal rules and state-level disclosure requirements
                  simultaneously.
                </p>
                <p className="text-xl md:text-[1.35rem] text-slate-600 leading-relaxed">
                  And the next exam doesn't care that the compliance team is the same size it was when
                  the bank was a fraction of today's footprint. Marketing approval queues back up.
                  HMDA field mappings break when a new product launches. Fair lending profiles diverge
                  before anyone notices. CRA assessment areas don't get re-delineated as new branches
                  open. Crossing into the Large Bank examination threshold is a step-change, not a
                  gradient.
                </p>
                <p className="text-xl md:text-[1.35rem] text-slate-600 leading-relaxed">
                  At the same time, AI is moving into marketing workflows, lending decisioning, and
                  customer communications faster than any institution's policy framework can keep up.
                  Every new capability creates new compliance exposure — and most institutions don't
                  have a systematic way to govern it without slowing the business down.
                </p>
                <p className="text-xl md:text-[1.35rem] font-semibold text-navy-900 mt-10">
                  Clarid is the technology layer that lets a community bank operate at scale without giving up the rigor that makes the bank defensible at exam time.
                </p>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ─── Section 3: Platform Capabilities ─── */}
      <section className="py-24 md:py-36">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <FadeUp>
            <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-16">
              What Clarid Does
            </h2>
          </FadeUp>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
            {capabilities.map((cap, i) => (
              <ScaleIn key={cap.title} delay={i * 0.1}>
                <div className="group p-10 lg:p-12 bg-slate-50 rounded-2xl border border-slate-100 hover:border-indigo-200 hover:shadow-xl transition-all duration-500 h-full">
                  <div className="flex items-center gap-4 mb-5">
                    <div className="w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center group-hover:bg-indigo-100 group-hover:scale-110 transition-all duration-300">
                      <cap.icon className="h-6 w-6 text-indigo-600" />
                    </div>
                    <h3 className="text-2xl font-display font-bold text-navy-900">{cap.title}</h3>
                  </div>
                  <p className="text-lg text-slate-500 leading-relaxed">{cap.description}</p>
                </div>
              </ScaleIn>
            ))}
          </div>

          <FadeUp delay={0.4}>
            <p className="mt-14 text-center text-lg text-slate-500 max-w-4xl mx-auto leading-relaxed">
              Clarid integrates into the tools the compliance team already uses — Microsoft Copilot,
              Claude, ChatGPT — through MCP (Model Context Protocol). No new portal to learn.
              Compliance intelligence embedded in daily workflows.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* ─── Section 4: Cross-Module Interconnectivity ─── */}
      <section className="py-24 md:py-36 bg-navy-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '48px 48px',
        }} />
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12 relative">
          <FadeUp>
            <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-white text-center mb-6">
              The Moat Is Cross-Module Interconnectivity
            </h2>
          </FadeUp>

          <FadeUp delay={0.1}>
            <p className="text-xl text-indigo-200 text-center max-w-4xl mx-auto mb-20 leading-relaxed">
              Legacy compliance vendors are batch-file-and-report shops. Each tool produces its own
              outputs from its own data. Clarid is built so every module feeds every other — one
              canonical loan-level data source powers fair lending, CRA, HMDA, and board reports.
            </p>
          </FadeUp>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-7 lg:gap-9 max-w-6xl mx-auto">
            {interconnectivityFlows.map((flow, i) => (
              <FadeUp key={flow.title} delay={i * 0.1}>
                <div className="p-9 bg-white/[0.04] rounded-2xl border border-white/10 backdrop-blur-sm hover:bg-white/[0.07] hover:border-indigo-400/30 transition-all duration-500 h-full">
                  <h3 className="text-xl font-display font-bold text-white mb-3">{flow.title}</h3>
                  <p className="text-base text-indigo-100/80 leading-relaxed">{flow.description}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Section 5: Why Clarid Fits SmartBank ─── */}
      <section className="py-24 md:py-36">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <FadeUp>
            <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-16">
              Why Clarid Fits SmartBank Specifically
            </h2>
          </FadeUp>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10 max-w-6xl mx-auto">
            {smartBankFitPoints.map((point, i) => (
              <ScaleIn key={point.title} delay={i * 0.1}>
                <div className="group p-10 lg:p-12 bg-white rounded-2xl border border-slate-200 hover:border-indigo-300 hover:shadow-xl transition-all duration-500 h-full">
                  <div className="flex items-center gap-4 mb-5">
                    <div className="w-14 h-14 rounded-xl bg-indigo-50 flex items-center justify-center group-hover:bg-indigo-100 group-hover:scale-110 transition-all duration-300">
                      <point.icon className="h-7 w-7 text-indigo-600" />
                    </div>
                    <h3 className="text-2xl font-display font-bold text-navy-900">{point.title}</h3>
                  </div>
                  <p className="text-lg text-slate-500 leading-relaxed">{point.description}</p>
                </div>
              </ScaleIn>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Section 6: Built by Examiners ─── */}
      <section className="py-24 md:py-36 bg-slate-50">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <div className="max-w-5xl mx-auto">
            <FadeUp>
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-14">
                Built From the Inside — by Examiners and Consultants
              </h2>
            </FadeUp>

            <FadeUp delay={0.1}>
              <div className="space-y-7">
                <p className="text-xl md:text-[1.35rem] text-slate-600 leading-relaxed">
                  Clarid's compliance methodology is built by people who spent careers in the seats
                  that grade banks. Sixty-plus combined years of FDIC, Federal Reserve, and
                  consulting-firm examiner experience are inside the rules engine — not approximated
                  from public regulatory specifications.
                </p>
                <p className="text-xl md:text-[1.35rem] text-slate-600 leading-relaxed">
                  The CRA methodology proves the standard. Clarid's FDIC Large Bank CRAD reproduced a
                  $2.1B sample bank's manual exam-grade Lending Test conclusion exactly — built from the
                  same loan-level data the consultant used, in minutes instead of weeks. Same
                  examiner-defensible math; faster, cheaper, and always current as new data lands.
                </p>
                <p className="text-xl md:text-[1.35rem] text-slate-600 leading-relaxed">
                  The same depth runs through every module. HMDA borrower-profile distribution uses
                  MSA-specific median family income proration. Fair lending statistical tests
                  (chi-square, Fisher's exact, Welch's t-test) are regulator-aware — FDIC, OCC, and
                  Federal Reserve methodologies are not interchangeable, and Clarid knows the
                  difference. Marketing compliance is a deterministic rules engine, not an AI guess;
                  the same input produces the same output every time, which is the only kind of
                  output an examiner trusts.
                </p>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ─── Section 7: Due Diligence Ready ─── */}
      <section className="py-24 md:py-36">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <div className="max-w-5xl mx-auto">
            <FadeUp>
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-6">
                Due Diligence Ready
              </h2>
            </FadeUp>

            <FadeUp delay={0.1}>
              <p className="text-xl text-slate-500 text-center max-w-3xl mx-auto mb-14 leading-relaxed">
                Vendor risk and IT review documentation prepared to community-bank standards, available
                on request.
              </p>
            </FadeUp>

            <FadeUp delay={0.2}>
              <div className="max-w-3xl mx-auto">
                <ul className="space-y-5">
                  {dueDiligenceDocs.map((doc, i) => (
                    <FadeIn key={doc} delay={0.25 + i * 0.05}>
                      <li className="flex items-start gap-4">
                        <CheckCircleIcon className="h-6 w-6 text-indigo-500 flex-shrink-0 mt-0.5" />
                        <span className="text-lg text-slate-600">{doc}</span>
                      </li>
                    </FadeIn>
                  ))}
                </ul>
              </div>
            </FadeUp>

            <FadeUp delay={0.6}>
              <p className="mt-12 text-center text-lg text-slate-500 max-w-4xl mx-auto leading-relaxed">
                SSL Labs A+ and SecurityHeaders.com A+ on both production domains. Zero production
                dependency vulnerabilities. SOC 2 Type II is on Clarid's 12-month roadmap — controls
                are being documented now.
              </p>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ─── Section 8: Moving Forward ─── */}
      <section className="py-24 md:py-36 bg-slate-50">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <div className="max-w-4xl mx-auto">
            <FadeUp>
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-16">
                Moving Forward
              </h2>
            </FadeUp>

            <div className="space-y-10 lg:space-y-12">
              {nextSteps.map((step, i) => (
                <FadeUp key={step.num} delay={i * 0.12}>
                  <div className="flex items-start gap-7">
                    <div className="flex-shrink-0 w-14 h-14 rounded-full bg-navy-900 text-white flex items-center justify-center text-lg font-bold">
                      {step.num}
                    </div>
                    <div className="pt-2">
                      <h3 className="text-xl md:text-2xl font-display font-bold text-navy-900 mb-2">{step.title}</h3>
                      <p className="text-lg text-slate-500 leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── Section 9: Contact ─── */}
      <section className="py-24 md:py-32">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12 text-center">
          <FadeIn>
            <div className="space-y-3">
              <p className="text-3xl font-display font-bold text-navy-900">Bobby Sloan</p>
              <p className="text-lg text-slate-500">Founder &amp; CEO, Clarid</p>
              <div className="flex items-center justify-center gap-3 mt-4 text-lg text-indigo-600">
                <EnvelopeIcon className="h-5 w-5" />
                <a href="mailto:bobby@clarid.ai" className="hover:text-indigo-700 transition-colors">
                  bobby@clarid.ai
                </a>
              </div>
              <p className="text-base text-slate-400 mt-1">clarid.ai</p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ─── Minimal Footer ─── */}
      <footer className="py-8 border-t border-slate-100">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12 flex flex-col sm:flex-row items-center justify-between gap-4">
          <img src="/clarid-logo.png" alt="Clarid" className="h-7 opacity-40" />
          <p className="text-slate-400 text-sm">
            &copy; {new Date().getFullYear()} Clarid. Prepared for SmartBank — Confidential.
          </p>
        </div>
      </footer>
    </div>
  );
}
